import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { useSideTittel } from "@/hooks/useSideTittel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  BarChart3,
  Plus,
  X,
  LogIn,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ComparisonCharts, { COLORS, FYLKE_COLOR } from "@/components/ComparisonCharts";
import { EndringarInline } from "@/components/EndringarNotice";
import { setEndringsMerkeListe, clearEndringsMerke } from "@/components/endringMerke";
import { dec } from "@/lib/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const formatNumber = (num) => {
  if (num === null || num === undefined) return "—";
  return new Intl.NumberFormat("nb-NO").format(num);
};

const ComparisonView = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fylker, setFylker] = useState([]);
  const [kommuner, setKommuner] = useState([]);
  const [selectedFylke, setSelectedFylke] = useState("");
  const [selectedKommuner, setSelectedKommuner] = useState([]);
  const [comparisonData, setComparisonData] = useState({});
  const [demographicsData, setDemographicsData] = useState({});
  const [oversiktData, setOversiktData] = useState({});
  const [boligData, setBoligData] = useState({});
  const [arbeidData, setArbeidData] = useState({});
  const [pendlingData, setPendlingData] = useState({});
  const [alderData, setAlderData] = useState({});
  const [korttidData, setKorttidData] = useState({});
  const [arbeidskapitalData, setArbeidskapitalData] = useState({});
  const [rangeringCompareData, setRangeringCompareData] = useState({});
  const [ulikskapCompareData, setUlikskapCompareData] = useState({});
  const [tjenesterCompareData, setTjenesterCompareData] = useState({});
  const valdeNamn = selectedKommuner
    .map((code) => comparisonData[code]?.name || kommuner.find((k) => k.kommune_code === code)?.kommune_name)
    .filter(Boolean);
  useSideTittel(
    valdeNamn.length ? `Samanlikn ${valdeNamn.join(", ")}` : "Samanlikn kommunar",
    valdeNamn.length
      ? `${valdeNamn.join(", ")} side om side: gjeld, driftsresultat, eigedomsskatt, gebyr, bemanning i barnehage, skule og eldreomsorg.`
      : "Vel inntil fem kommunar og samanlikn gjeld, driftsresultat, gebyr og bemanning side om side."
  );
  const [eiendomCompareData, setEiendomCompareData] = useState({});
  const [finanskostnadCompareData, setFinanskostnadCompareData] = useState({});
  const [endringarListe, setEndringarListe] = useState([]);
  const [fylkeAverageData, setFylkeAverageData] = useState(null);
  const [showFylkeAverage, setShowFylkeAverage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dataRefreshing, setDataRefreshing] = useState(false);
  const [incompleteKommuner, setIncompleteKommuner] = useState([]);
  const pollRef = useRef(null);

  // Fylkessnitt når alle valgte kommuner ligger i samme fylke
  useEffect(() => {
    const fylkeCodes = new Set(selectedKommuner.map((code) => kommuner.find((k) => k.kommune_code === code)?.fylke_code).filter(Boolean));
    if (fylkeCodes.size !== 1) {
      setFylkeAverageData(null);
      return;
    }
    axios.get(`${API}/finance/fylke-average/${[...fylkeCodes][0]}`)
      .then((res) => setFylkeAverageData(res.data))
      .catch(() => setFylkeAverageData(null));
  }, [selectedKommuner, kommuner]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setDataRefreshing(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Initialize from URL params
  useEffect(() => {
    const codes = searchParams.get("kommuner");
    if (codes) {
      setSelectedKommuner((prev) => (prev.join(",") === codes ? prev : codes.split(",")));
    }
  }, [searchParams]);

  const fetchFylker = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fylker`);
      setFylker(response.data);
    } catch (error) {
      console.error("Error fetching fylker:", error);
    }
  }, []);

  const fetchKommuner = useCallback(async (fylkeCode = null) => {
    try {
      const url = fylkeCode && fylkeCode !== "all"
        ? `${API}/kommuner?fylke_code=${fylkeCode}`
        : `${API}/kommuner`;
      const response = await axios.get(url);
      setKommuner(response.data);
    } catch (error) {
      console.error("Error fetching kommunar:", error);
    }
  }, []);

  const fetchComparisonData = useCallback(async () => {
    stopPolling();
    clearEndringsMerke();
    if (selectedKommuner.length === 0) {
      setComparisonData({});
      setDemographicsData({});
      setOversiktData({});
      setBoligData({});
      setArbeidData({});
      setPendlingData({});
      setAlderData({});
      setKorttidData({});
      setArbeidskapitalData({});
      setRangeringCompareData({});
      setUlikskapCompareData({});
      setIncompleteKommuner([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch finance comparison data
      const response = await axios.get(
        `${API}/finance/compare/${selectedKommuner.join(",")}`
      );
      setComparisonData(response.data);
      
      const codes = selectedKommuner.join(",");
      const loadSecondary = async () => {
        const demographicsResponses = await Promise.all(
          selectedKommuner.map((code) => axios.get(`${API}/demographics/${code}`).catch(() => ({ data: null })))
        );
        const demographics = {};
        selectedKommuner.forEach((code, index) => {
          if (demographicsResponses[index].data) demographics[code] = demographicsResponses[index].data;
        });
        setDemographicsData(demographics);
        axios.get(`${API}/okonomisk-oversikt/compare/${codes}`).then((res) => setOversiktData(res.data)).catch(() => setOversiktData({}));
        axios.get(`${API}/bolig/compare/${codes}`).then((res) => setBoligData(res.data)).catch(() => setBoligData({}));
        axios.get(`${API}/arbeid/compare/${codes}`).then((res) => setArbeidData(res.data)).catch(() => setArbeidData({}));
        axios.get(`${API}/pendling/compare/${codes}`).then((res) => setPendlingData(res.data)).catch(() => setPendlingData({}));
        axios.get(`${API}/alder/compare/${codes}`).then((res) => setAlderData(res.data)).catch(() => setAlderData({}));
        axios.get(`${API}/korttid/compare/${codes}`).then((res) => setKorttidData(res.data)).catch(() => setKorttidData({}));
        axios.get(`${API}/arbeidskapital/compare/${codes}`).then((res) => setArbeidskapitalData(res.data)).catch(() => setArbeidskapitalData({}));
        axios.get(`${API}/rangering/compare/${codes}`).then((res) => setRangeringCompareData(res.data)).catch(() => setRangeringCompareData({}));
        axios.get(`${API}/ulikskap/compare/${codes}`).then((res) => setUlikskapCompareData(res.data)).catch(() => setUlikskapCompareData({}));
        axios.get(`${API}/tjenester/compare/${codes}`).then((res) => setTjenesterCompareData(res.data)).catch(() => setTjenesterCompareData({}));
        axios.get(`${API}/eiendom/compare/${codes}`).then((res) => setEiendomCompareData(res.data)).catch(() => setEiendomCompareData({}));
        axios.get(`${API}/finanskostnad/compare/${codes}`).then((res) => setFinanskostnadCompareData(res.data)).catch(() => setFinanskostnadCompareData({}));
        Promise.all(codes.split(",").map((c) => axios.get(`${API}/endringar/${c}`).then((r) => r.data).catch(() => null)))
          .then((liste) => {
            const reine = liste.filter(Boolean);
            setEndringsMerkeListe(reine);
            setEndringarListe(reine);
          });
      };
      await loadSecondary();

      // Kommuner som mangler finans- eller befolkningstall: backend henter i bakgrunnen, vi poller til alt er på plass
      const incomplete = Object.entries(response.data)
        .filter(([_, data]) => data.data_status === "incomplete")
        .map(([code, _]) => code);
      setIncompleteKommuner(incomplete);

      if (incomplete.length > 0) {
        setDataRefreshing(true);
        const maxAttempts = 30; // 30 × 4 s = 2 min (SSB er rate-limited)
        let attempts = 0;
        pollRef.current = setInterval(async () => {
          attempts++;
          try {
            const refreshResponse = await axios.get(`${API}/finance/compare/${codes}`);
            const stillIncomplete = Object.entries(refreshResponse.data)
              .filter(([_, data]) => data.data_status === "incomplete")
              .map(([code, _]) => code);
            setComparisonData(refreshResponse.data);
            setIncompleteKommuner(stillIncomplete);
            if (stillIncomplete.length === 0 || attempts >= maxAttempts) {
              stopPolling();
              await loadSecondary();
            }
          } catch (err) {
            console.error("Error polling for data:", err);
            if (attempts >= maxAttempts) stopPolling();
          }
        }, 4000);
      }

    } catch (error) {
      console.error("Error fetching comparison data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedKommuner, stopPolling]);

  useEffect(() => {
    fetchFylker();
    fetchKommuner();
  }, [fetchFylker, fetchKommuner]);

  useEffect(() => {
    if (selectedFylke) {
      fetchKommuner(selectedFylke);
    }
  }, [selectedFylke, fetchKommuner]);

  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  // Hold URL i takt med valgte kommuner (uten å utløse ny henting)
  useEffect(() => {
    const current = searchParams.get("kommuner") || "";
    const next = selectedKommuner.join(",");
    if (current === next) return;
    setSearchParams(next ? { kommuner: next } : {});
  }, [selectedKommuner, searchParams, setSearchParams]);

  const addKommune = (kommuneCode) => {
    if (!selectedKommuner.includes(kommuneCode) && selectedKommuner.length < 5) {
      setSelectedKommuner([...selectedKommuner, kommuneCode]);
    }
  };

  const removeKommune = (kommuneCode) => {
    setSelectedKommuner(selectedKommuner.filter((k) => k !== kommuneCode));
  };

  const getKommuneName = (code) => {
    const kommune = kommuner.find((k) => k.kommune_code === code);
    return kommune?.kommune_name || code;
  };

  // Prepare chart data
  const getLatestMetrics = () => {
    const metrics = Object.entries(comparisonData).map(([code, kommune], idx) => {
      const latestData = kommune.data?.[kommune.data.length - 1];
      const driftsmarginNum =
        latestData?.inntekt && latestData?.kostnad
          ? ((latestData.inntekt - latestData.kostnad) / latestData.inntekt) * 100
          : null;
      const driftsmargin = driftsmarginNum == null ? null : dec(driftsmarginNum, 2);
      // Total gjeld = kortsiktig gjeld (gjeld) + langsiktig lån (laan)
      const totalGjeld = (latestData?.gjeld || 0) + (latestData?.laan || 0);
      const gjeldsandel =
        latestData?.anleggsmidler && totalGjeld > 0
          ? dec(((totalGjeld / latestData.anleggsmidler) * 100), 2)
          : null;

      return {
        code,
        name: kommune.name,
        color: COLORS[idx % COLORS.length],
        year: latestData?.year,
        inntekt: latestData?.inntekt,
        kostnad: latestData?.kostnad,
        driftsmargin,
        driftsmarginNum,
        gjeld: totalGjeld > 0 ? totalGjeld : null,
        laan: latestData?.laan,
        kortsiktigGjeld: latestData?.gjeld,
        gjeldsandel,
        isFylkeAverage: false,
      };
    });

    // Add fylke average if available
    if (showFylkeAverage && fylkeAverageData?.data?.length > 0) {
      const latestAvg = fylkeAverageData.data[fylkeAverageData.data.length - 1];
      const driftsmarginNum =
        latestAvg?.inntekt && latestAvg?.kostnad
          ? ((latestAvg.inntekt - latestAvg.kostnad) / latestAvg.inntekt) * 100
          : null;
      const driftsmargin = driftsmarginNum == null ? null : dec(driftsmarginNum, 2);
      // Total gjeld for fylkessnitt = gjeld + laan
      const fylkeTotalGjeld = (latestAvg?.gjeld || 0) + (latestAvg?.laan || 0);
      const gjeldsandel =
        latestAvg?.anleggsmidler && fylkeTotalGjeld > 0
          ? dec(((fylkeTotalGjeld / latestAvg.anleggsmidler) * 100), 2)
          : null;

      metrics.push({
        code: "fylke_avg",
        name: fylkeAverageData.name,
        color: FYLKE_COLOR,
        year: latestAvg?.year,
        inntekt: latestAvg?.inntekt,
        kostnad: latestAvg?.kostnad,
        driftsmargin,
        driftsmarginNum,
        gjeld: fylkeTotalGjeld > 0 ? fylkeTotalGjeld : null,
        laan: latestAvg?.laan,
        kortsiktigGjeld: latestAvg?.gjeld,
        gjeldsandel,
        isFylkeAverage: true,
        kommuneCount: latestAvg?.kommune_count,
      });
    }

    return metrics;
  };

  // Prepare median income chart data (horizontal bar chart)
  const latestMetrics = getLatestMetrics();

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#103766]" />
                <h1 className="text-xl font-semibold text-[#1F2A36]">
                  Samanlikn kommunar
                </h1>
              </div>
            </div>

            {!user && (
              <Button
                variant="outline"
                size="sm"
                onClick={login}
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Logg inn
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selection Card */}
        <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-lg font-medium text-[#1F2A36]">
              Vel kommunar å samanlikne (maks 5)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2 block">
                  Fylke
                </label>
                <Select value={selectedFylke} onValueChange={setSelectedFylke}>
                  <SelectTrigger className="w-full border-[#E6E9F0]">
                    <SelectValue placeholder="Alle fylke" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle fylke</SelectItem>
                    {fylker.map((f) => (
                      <SelectItem key={f.fylke_code} value={f.fylke_code}>
                        {f.fylke_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2 block">
                  Legg til kommune
                </label>
                <Select
                  value=""
                  onValueChange={addKommune}
                  disabled={selectedKommuner.length >= 5}
                >
                  <SelectTrigger className="w-full border-[#E6E9F0]">
                    <SelectValue placeholder="Vel kommune..." />
                  </SelectTrigger>
                  <SelectContent>
                    {kommuner
                      .filter((k) => !selectedKommuner.includes(k.kommune_code))
                      .map((k) => (
                        <SelectItem key={k.kommune_code} value={k.kommune_code}>
                          {k.kommune_name} ({k.fylke_name})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Selected kommuner */}
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedKommuner.map((code, idx) => (
                <Badge
                  key={code}
                  variant="secondary"
                  className="px-3 py-1.5 text-sm flex items-center gap-2"
                  style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20`, color: COLORS[idx % COLORS.length] }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <Link 
                    to={`/kommune/${code}`}
                    className="hover:underline"
                  >
                    {getKommuneName(code)}
                  </Link>
                  {incompleteKommuner.includes(code) && (
                    <span className="text-xs opacity-70">(hentar data...)</span>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeKommune(code);
                    }}
                    className="ml-1 hover:opacity-70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {selectedKommuner.length === 0 && (
                <p className="text-sm text-[#8AA4BF]">
                  Vel kommunar frå lista over for å samanlikne
                </p>
              )}
            </div>
            
            {/* Data refresh indicator */}
            {dataRefreshing && incompleteKommuner.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                <span className="text-sm text-blue-700">
                  Henter data for {incompleteKommuner.length} kommune{incompleteKommuner.length > 1 ? 'r' : ''}...
                </span>
              </div>
            )}

            {/* Fylke average toggle */}
            {fylkeAverageData && (
              <div className="flex items-center space-x-2 pt-4 border-t border-[#E6E9F0]">
                <Checkbox
                  id="fylke-average"
                  checked={showFylkeAverage}
                  onCheckedChange={setShowFylkeAverage}
                />
                <Label htmlFor="fylke-average" className="text-sm text-[#4B647F] cursor-pointer">
                  Vis fylkessnitt ({fylkeAverageData.name})
                </Label>
              </div>
            )}
          </CardContent>
        </Card>

        {selectedKommuner.length > 0 && (
          <>
            {/* Metrics Table */}
            <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8">
              <CardHeader className="border-b border-[#E6E9F0] pb-4">
                <CardTitle className="text-lg font-medium text-[#1F2A36]">
                  Nøkkeltal samanlikning
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F8FB]">
                        <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Kommune
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Inntekter
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Utgifter
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Driftsmargin
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Total gjeld
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">
                          Gjeldsandel
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-[#8AA4BF]">
                            Lastar data...
                          </td>
                        </tr>
                      ) : (
                        latestMetrics.map((m) => (
                          <tr
                            key={m.code}
                            className={`border-t border-[#E6E9F0] hover:bg-[#F6F8FB]/50 ${m.isFylkeAverage ? "bg-[#F6F8FB]" : ""}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-3 h-3 rounded-full ${m.isFylkeAverage ? "border-2 border-dashed border-[#9CA3AF]" : ""}`}
                                  style={{ backgroundColor: m.isFylkeAverage ? "transparent" : m.color }}
                                />
                                {m.isFylkeAverage ? (
                                  <span className="font-medium text-[#4B647F] italic">
                                    {m.name}
                                  </span>
                                ) : (
                                  <Link 
                                    to={`/kommune/${m.code}`}
                                    className="font-medium text-[#1F2A36] hover:text-[#103766] hover:underline"
                                  >
                                    {m.name}
                                  </Link>
                                )}
                                <span className="text-xs text-[#8AA4BF]">
                                  ({m.year})
                                  {m.kommuneCount && ` • ${m.kommuneCount} kommunar`}
                                </span>
                              </div>
                            </td>
                            <td className={`py-3 px-4 text-right ${m.isFylkeAverage ? "text-[#4B647F]" : "text-[#1F2A36]"}`}>
                              {formatNumber(m.inntekt ? Math.round(m.inntekt) : null)}
                            </td>
                            <td className={`py-3 px-4 text-right ${m.isFylkeAverage ? "text-[#4B647F]" : "text-[#1F2A36]"}`}>
                              {formatNumber(m.kostnad ? Math.round(m.kostnad) : null)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <span
                                  className={
                                    m.driftsmarginNum != null && m.driftsmarginNum >= 0
                                      ? "text-[#3F8A86]"
                                      : "text-[#D35E4E]"
                                  }
                                >
                                  {m.driftsmargin ? `${m.driftsmargin}%` : "—"}
                                </span>
                                {m.driftsmargin &&
                                  (m.driftsmarginNum >= 0 ? (
                                    <TrendingUp className="w-4 h-4 text-[#3F8A86]" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4 text-[#D35E4E]" />
                                  ))}
                              </div>
                            </td>
                            <td className={`py-3 px-4 text-right ${m.isFylkeAverage ? "text-[#4B647F]" : "text-[#1F2A36]"}`}>
                              {formatNumber(m.gjeld ? Math.round(m.gjeld) : null)}
                            </td>
                            <td className="py-3 px-4 text-right text-[#1F2A36]">
                              {m.gjeldsandel ? `${m.gjeldsandel}%` : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="mb-4">
              <EndringarInline liste={endringarListe} />
            </div>

            <ComparisonCharts
              comparisonData={comparisonData}
              demographicsData={demographicsData}
              fylkeAverageData={fylkeAverageData}
              showFylkeAverage={showFylkeAverage}
              oversiktData={oversiktData}
              boligData={boligData}
              arbeidData={arbeidData}
              pendlingData={pendlingData}
              alderData={alderData}
              korttidData={korttidData}
              arbeidskapitalData={arbeidskapitalData}
              rangeringCompareData={rangeringCompareData}
              ulikskapCompareData={ulikskapCompareData}
              tjenesterCompareData={tjenesterCompareData}
              eiendomCompareData={eiendomCompareData}
              finanskostnadCompareData={finanskostnadCompareData}
            />
          </>
        )}
      </main>
    </div>
  );
};

export default ComparisonView;
